import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { TextField } from "@mui/material";
import { ThreeDots } from "react-loader-spinner";
import dynamic from "next/dynamic";
import emailjs from "@emailjs/browser";
import styles from "./Contact.module.scss";
import { slideIn } from "@/utils/Motion";
import { F_Open_Sans, F_Ubuntu } from "@/fonts";

const PopUpMessage = dynamic(
  () => import("../../elements/PopUpMessage/PopUpMessage"),
  { ssr: false },
);

interface FormState {
  name: string;
  email: string;
  message: string;
}

const textFieldSX = {
  label: { color: "white" },
  fieldset: { borderColor: "white" },
  "& .MuiOutlinedInput-root": {
    "&.Mui-focused fieldset": { borderColor: "white" },
    "&:hover fieldset": { borderColor: "white" },
  },
};

const Contact: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const [form, setForm] = useState<FormState>({
    name: "",
    email: "",
    message: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    emailjs
      .send(
        process.env.NEXT_PUBLIC_EMAILJS_SERVICE_ID!,
        process.env.NEXT_PUBLIC_EMAILJS_TEMPLATE_ID!,
        {
          from_name: form.name,
          to_name: "Jasmina",
          from_email: form.email,
          to_email: process.env.NEXT_PUBLIC_MY_OUTLOOK_EMAIL!,
          message: form.message,
        },
        process.env.NEXT_PUBLIC_EMAILJS_PUBLIC_KEY!,
      )
      .then(
        () => {
          setLoading(false);
          setSuccess(true);
          setForm({ name: "", email: "", message: "" });
        },
        () => {
          setLoading(false);
          setError(true);
        },
      );
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.1 },
    );

    const element = document.getElementById("Contact");
    if (element) observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, []);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(false);
        setError(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  return (
    <>
      {success && (
        <PopUpMessage
          title="Thank you for reaching out to me!"
          text="I appreciate your interest and taking the time to connect. Your message has been received, and I will make sure to respond as soon as possible."
        />
      )}
      {error && (
        <PopUpMessage
          title="Sorry"
          text="Something went wrong. Please try again."
        />
      )}

      <div
        id="Contact"
        className={`${styles.contact} ${F_Open_Sans.className}`}
      >
        <motion.div
          variants={slideIn("left", "tween", 0.2, 1)}
          initial="hidden"
          animate={isVisible ? "show" : "hidden"}
          className={styles.contactPage}
        >
          <div className={styles.contactText}>
            <h3 className={styles.sectionHeadText}>Contact me</h3>
          </div>

          <div className={`${styles.contactWrapper} ${F_Ubuntu.className}`}>
            <div className={styles.contactOptionsContainer}>
              <div className={styles.contactForm}>
                <form onSubmit={handleSubmit}>
                  <div className={styles.inputsWrapper}>
                    <TextField
                      sx={textFieldSX}
                      className={styles.input}
                      required
                      id="name"
                      value={form.name}
                      label="Name"
                      onChange={handleChange}
                      type="text"
                      name="name"
                    />
                    <TextField
                      sx={textFieldSX}
                      className={styles.input}
                      required
                      id="email"
                      label="E-mail"
                      value={form.email}
                      onChange={handleChange}
                      type="email"
                      name="email"
                    />
                  </div>

                  <TextField
                    sx={textFieldSX}
                    className={styles.textArea}
                    multiline
                    rows={5}
                    value={form.message}
                    required
                    id="message"
                    label="Message"
                    onChange={handleChange}
                    name="message"
                  />

                  <button
                    className={
                      loading ? styles.contactBtnLoading : styles.contactBtn
                    }
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        Sending{" "}
                        <ThreeDots
                          height="50"
                          width="50"
                          radius="9"
                          color="#161854"
                          ariaLabel="three-dots-loading"
                          wrapperStyle={{}}
                          visible={true}
                        />
                      </>
                    ) : (
                      "Send"
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default Contact;
